package com.example.mystybag.login.logic

import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_BIOMETRIC_PROMPT_DECLINED
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_CREATE_BAGS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_VIEW_REVENUE
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedActivity
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.security.Security
import com.example.mystybag.core.security.isJwtExpired
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.NavigatorConfig
import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.biometricUtils.helpers.BiometricErrorResult
import com.example.mystybag.common.logic.NavigatorStaffBiometric
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.login.model.StaffBiometricUIState
import com.example.mystybag.sharedmodels.project.requestModels.merchant.LoginStaffRequestModel
import kotlinx.coroutines.launch

class StaffBiometricViewModel(
    context: AppContext,
    private val config: NavigatorConfig? = null
) : ChildViewModel<StaffBiometricUIState>(context, StaffBiometricUIState()), LoginInterface {

    private val passwordToSaveAfterBiometric: String? =
        (config as? NavigatorStaffBiometric)?.passwordToSaveAfterBiometric
    private val sessionFromLogin: Triple<String, Long, String>? = (config as? NavigatorStaffBiometric)?.let { n ->
        if (n.jwt.isNotEmpty() && n.merchantId != 0L) Triple(n.jwt, n.merchantId, n.staffLocationIds) else null
    }

    override fun onCreate() {
        navControls.backButtonVisible = false

        val biometricType = BiometricsHandler.getAuthenticationType()
        updateState { it.copy(biometricType = biometricType) }

        val biometricAvailable = biometricType != BiometricAuthenticationType.NONE && BiometricsHandler.isBiometricScannerAvailableAndSet()

        if (sessionFromLogin != null || passwordToSaveAfterBiometric != null) {
            if (biometricAvailable) {
                updateState { it.copy(showEnableScreen = true) }
            } else {
                onSkipBiometric()
            }
            return
        }

        val useBiometricForStaff = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_STAFF, false)
        val merchantJwt = Preferences.getString(key = com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT, "")

        if (merchantJwt.isNotEmpty() && isJwtExpired(merchantJwt)) {
            Preferences.setString(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT, "")
            launchOnMain { onBackButtonClicked() }
            return
        }

        if (merchantJwt.isNotEmpty() && useBiometricForStaff && Preferences.getBoolean(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF, false)) {
            if (biometricAvailable) {
                startBiometricAuthentication()
            } else {
                onSkipBiometric()
            }
            return
        }

        if (merchantJwt.isEmpty() && useBiometricForStaff && biometricAvailable) {
            val storedEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")
            val storedPassword = Preferences.getSecureString(key = PREFERENCES_STAFF_PASSWORD, defaultValue = "")
            if (storedEmail.isNotEmpty() && storedPassword.isNotEmpty()) {
                startBiometricAuthentication()
                return
            }
            launchOnMain { onBackButtonClicked() }
            return
        }

        if (biometricAvailable) {
            updateState { it.copy(showEnableScreen = true) }
        } else {
            onSkipBiometric()
        }
    }

    fun onSetUpBiometric() {
        enableBiometric()
    }

    private fun startBiometricAuthentication() {
        val started = BiometricsHandler.startBiometricAuthentication(
            activity = sharedActivity,
            onSuccess = { performLoginAfterBiometric() },
            onFailed = { error ->
                when (error) {
                    BiometricErrorResult.USER_CANCELED -> onBackButtonClicked()
                    else -> {
                        showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                        onBackButtonClicked()
                    }
                }
            }
        )
        if (!started) {
            onSkipBiometric()
        }
    }

    private fun enableBiometric() {
        launchOnMain {
            BiometricsHandler.startSettingBiometricAuthentication(
                activity = sharedActivity,
                onSuccess = {
                    BiometricsHandler.storeFinalKey()
                    Preferences.setBoolean(PREFERENCES_USE_BIOMETRIC_FOR_STAFF, true)
                    passwordToSaveAfterBiometric?.let { pwd ->
                        try {
                            Preferences.setSecureString(PREFERENCES_STAFF_PASSWORD, pwd)
                        } catch (e: Throwable) {
                            LogHelper.logEvent("[StaffBiometric] setSecureString(password) failed: ${e.message}")
                        }
                    }
                    onSkipBiometric()
                },
                onFailed = { error ->
                    when (error) {
                        BiometricErrorResult.USER_CANCELED -> onSkipBiometric()
                        else -> {
                            showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                            onSkipBiometric()
                        }
                    }
                }
            )
        }
    }

    private fun performLoginAfterBiometric() {
        val storedEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")
        val storedPassword = Preferences.getSecureString(key = PREFERENCES_STAFF_PASSWORD, defaultValue = "")
        if (storedEmail.isEmpty() || storedPassword.isEmpty()) {
            launchOnMain {
                showSnackbar(sharedString(SR.strings.please_log_in_again))
                onBackButtonClicked()
            }
            return
        }
        scope.launch {
            launchOnMain { updateState { it.copy(isLoading = true) } }
            commandBlock(showProgress = true) {
                CommonCommands.loginStaff(
                    LoginStaffRequestModel(email = storedEmail, password = Security.hashPasswordForRequest(storedPassword))
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                val loginResponse = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.LoginStaffResponseModel
                                if (loginResponse != null) {
                                    Communication.token = loginResponse.jwt
                                    Preferences.setString(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT, loginResponse.jwt)
                                    Preferences.setLong(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID, loginResponse.merchantId)
                                    Preferences.setBoolean(PREFERENCES_IS_STAFF, true)
                                    Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, loginResponse.merchantLocationIds.joinToString(","))
                                    Preferences.setBoolean(PREFERENCES_STAFF_CAN_CREATE_BAGS, loginResponse.canCreateBags)
                                    Preferences.setBoolean(PREFERENCES_STAFF_CAN_VIEW_REVENUE, loginResponse.canViewRevenue)
                                    if (loginResponse.passwordIsTemporary) {
                                        navigate(com.example.mystybag.common.logic.NavigatorStaffChangePassword)
                                    } else {
                                        setResult<LoginInterface> { goToMainScreen(false) }
                                    }
                                } else {
                                    showSnackbar(sharedString(SR.strings.please_log_in_again))
                                    onBackButtonClicked()
                                }
                            }
                        },
                        baseError = { err ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(err.status, err.apiEndPoint, err.statusMessage))
                                onBackButtonClicked()
                            }
                        }
                    )
            }
        }
    }

    fun onSkipBiometric() {
        launchOnMain {
            Preferences.setBoolean(PREFERENCES_STAFF_BIOMETRIC_PROMPT_DECLINED, true)
            when (val session = sessionFromLogin) {
                null -> {
                    val prefsJwt = Preferences.getString(key = com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT, "")
                    if (prefsJwt.isNotEmpty() && isJwtExpired(prefsJwt)) {
                        Preferences.setString(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT, "")
                        onBackButtonClicked()
                        return@launchOnMain
                    }
                    if (prefsJwt.isNotEmpty()) {
                        Communication.token = prefsJwt
                    }
                    setResult<LoginInterface> { goToMainScreen(false) }
                }
                else -> {
                    Communication.token = session.first
                    Preferences.setString(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT, session.first)
                    Preferences.setLong(com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID, session.second)
                    Preferences.setBoolean(PREFERENCES_IS_STAFF, true)
                    Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, session.third)
                    setResult<LoginInterface> { goToMainScreen(false) }
                }
            }
        }
    }

    override fun goToMainScreen(showProgress: Boolean, onErrorCallback: (() -> Unit)?, target: NavigatorConfig?) {
        setResult<LoginInterface> { goToMainScreen(showProgress, onErrorCallback, target) }
    }
}
